import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  Loader2,
  Download,
  Mail,
  Sparkles,
  Info,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  list1099ForYear,
  generate1099,
  download1099Url,
  send1099Email,
} from "@/utils/form-1099.functions";

export const Route = createFileRoute("/_app/payroll/tax-forms")({
  component: TaxFormsRoute,
});

type ListResult = Awaited<ReturnType<typeof list1099ForYear>>;
type Worker = ListResult["workers"][number];

// The IRS reporting threshold for a 1099-NEC is $600 (60000 cents).
const MUST_FILE_CENTS = 60000;

function TaxFormsRoute() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");

  const listFn = useServerFn(list1099ForYear);
  const generateFn = useServerFn(generate1099);
  const downloadFn = useServerFn(download1099Url);
  const sendFn = useServerFn(send1099Email);

  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState<number>(currentYear);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-worker editable Box 1 adjustment, keyed by userId (string for the input).
  const [adjustments, setAdjustments] = useState<Record<string, string>>({});
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);

  // Admin-only screen — mirror the guard used by the other _app.payroll.* routes.
  useEffect(() => {
    if (!isAdmin) {
      toast.error(t("pages.payroll.new_page.errors.adminOnly"));
      navigate({ to: "/payroll" });
    }
  }, [isAdmin, navigate, t]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listFn({ data: { taxYear } });
      const list = res.workers ?? [];
      setWorkers(list);
      const adj: Record<string, string> = {};
      for (const w of list) {
        const cents = w.existing?.box1Cents ?? w.computedCents;
        adj[w.userId] = (cents / 100).toFixed(2);
      }
      setAdjustments(adj);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.taxForms.toasts.loadFailed"));
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxYear, isAdmin]);

  const fmtUSD = (cents: number) =>
    (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

  // Current adjusted Box 1 value (in cents) for a worker, from the editable input.
  const adjustedCents = (w: Worker) => {
    const raw = adjustments[w.userId];
    const parsed = raw === undefined || raw === "" ? NaN : Number(raw);
    if (Number.isNaN(parsed)) return w.existing?.box1Cents ?? w.computedCents;
    return Math.round(parsed * 100);
  };

  const mustFile = (w: Worker) =>
    w.computedCents >= MUST_FILE_CENTS || adjustedCents(w) >= MUST_FILE_CENTS;

  const missingText = (w: Worker) =>
    w.missing.map((code) => t(`pages.taxForms.missing.${code}`)).join(", ");

  const handleGenerate = async (w: Worker) => {
    setGeneratingId(w.userId);
    try {
      await generateFn({
        data: { userId: w.userId, taxYear, box1Cents: adjustedCents(w), box4Cents: 0 },
      });
      toast.success(t("pages.taxForms.toasts.generated"));
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.taxForms.toasts.generateFailed"));
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDownload = async (w: Worker) => {
    setDownloadingId(w.userId);
    try {
      const res = await downloadFn({ data: { userId: w.userId, taxYear } });
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.taxForms.toasts.downloadFailed"));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSend = async (w: Worker) => {
    setSendingId(w.userId);
    try {
      const res = await sendFn({ data: { userId: w.userId, taxYear } });
      if (res.ok) {
        toast.success(t("pages.taxForms.toasts.emailSent"));
        await load();
      } else if (res.error === "no_email") {
        toast.error(t("pages.taxForms.toasts.noEmail"));
      } else {
        toast.error(t("pages.taxForms.toasts.emailFailed"));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.taxForms.toasts.emailFailed"));
    } finally {
      setSendingId(null);
    }
  };

  const handleGenerateAll = async () => {
    const eligible = workers.filter(
      (w) => w.missing.length === 0 && w.computedCents >= MUST_FILE_CENTS,
    );
    if (eligible.length === 0) {
      toast.info(t("pages.taxForms.toasts.nothingEligible"));
      return;
    }
    setBulkGenerating(true);
    let done = 0;
    try {
      for (const w of eligible) {
        await generateFn({
          data: { userId: w.userId, taxYear, box1Cents: adjustedCents(w), box4Cents: 0 },
        });
        done += 1;
      }
      toast.success(t("pages.taxForms.toasts.bulkGenerated", { count: done }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.taxForms.toasts.generateFailed"));
    } finally {
      setBulkGenerating(false);
      await load();
    }
  };

  const statusLabel = (status: string | undefined) => {
    if (!status) return "—";
    return t(`pages.taxForms.status.${status}`, { defaultValue: status });
  };

  const eligibleCount = workers.filter(
    (w) => w.missing.length === 0 && w.computedCents >= MUST_FILE_CENTS,
  ).length;

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/payroll"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> {t("pages.taxForms.back")}
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("pages.taxForms.title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.taxForms.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={String(taxYear)}
              onValueChange={(v) => setTaxYear(Number(v))}
              disabled={bulkGenerating}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t("pages.taxForms.yearLabel")} />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleGenerateAll}
              disabled={bulkGenerating || loading || eligibleCount === 0}
              className="gap-2"
            >
              {bulkGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {t("pages.taxForms.generateAll", { count: eligibleCount })}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-muted-foreground">{t("pages.taxForms.complianceNote")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("pages.taxForms.workersCard", { year: taxYear })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              {t("common.loading")}
            </p>
          ) : workers.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t("pages.taxForms.empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-2">{t("pages.taxForms.tableWorker")}</th>
                    <th className="py-2 pr-2">{t("pages.taxForms.tableW9")}</th>
                    <th className="py-2 pr-2 text-right">{t("pages.taxForms.tableTotalPaid")}</th>
                    <th className="py-2 pr-2 text-right">{t("pages.taxForms.tableAdjustment")}</th>
                    <th className="py-2 pr-2">{t("pages.taxForms.tableStatus")}</th>
                    <th className="py-2 pr-2 text-right">{t("pages.taxForms.tableActions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {workers.map((w) => {
                    const isMissing = w.missing.length > 0;
                    const busy = generatingId === w.userId || bulkGenerating;
                    return (
                      <tr key={w.userId}>
                        <td className="py-2 pr-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{w.name || "—"}</span>
                            {mustFile(w) && (
                              <Badge
                                variant="outline"
                                className="border-amber-500/40 text-amber-600"
                              >
                                {t("pages.taxForms.mustFile")}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-2">
                          {w.hasTin ? (
                            <Badge
                              variant="outline"
                              className="gap-1 border-primary/40 text-primary"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              {t("pages.taxForms.w9Received")}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-2 text-right tabular-nums">
                          {fmtUSD(w.computedCents)}
                        </td>
                        <td className="py-2 pr-2 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={adjustments[w.userId] ?? ""}
                            onChange={(e) =>
                              setAdjustments((prev) => ({ ...prev, [w.userId]: e.target.value }))
                            }
                            className="ml-auto h-8 w-28 text-right"
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <span className="text-muted-foreground">
                            {statusLabel(w.existing?.status)}
                          </span>
                        </td>
                        <td className="py-2 pr-1">
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex flex-wrap justify-end gap-1">
                              {isMissing ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled
                                  className="gap-1"
                                  title={missingText(w)}
                                >
                                  <Sparkles className="h-3.5 w-3.5" />
                                  {t("pages.taxForms.generate")}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleGenerate(w)}
                                  disabled={busy}
                                  className="gap-1"
                                >
                                  {generatingId === w.userId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-3.5 w-3.5" />
                                  )}
                                  {w.existing
                                    ? t("pages.taxForms.regenerate")
                                    : t("pages.taxForms.generate")}
                                </Button>
                              )}
                              {w.existing && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1"
                                    onClick={() => handleDownload(w)}
                                    disabled={downloadingId === w.userId}
                                  >
                                    {downloadingId === w.userId ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Download className="h-3.5 w-3.5" />
                                    )}
                                    {t("pages.taxForms.download")}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1"
                                    onClick={() => handleSend(w)}
                                    disabled={sendingId === w.userId}
                                  >
                                    {sendingId === w.userId ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Mail className="h-3.5 w-3.5" />
                                    )}
                                    {t("pages.taxForms.sendEmail")}
                                  </Button>
                                </>
                              )}
                            </div>
                            {isMissing && (
                              <p className="flex items-start justify-end gap-1 text-xs text-muted-foreground">
                                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                                <span>
                                  {t("pages.taxForms.missingPrefix")} {missingText(w)}
                                </span>
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
